import axios from 'axios';
import Cookies from 'js-cookie';
import authApi from '@/services/auth_api.ts';
import { useSchoolStore } from '@/stores/school_store.ts';
import { selectedAcademicYearRequestId, clearAcademicYearHeader } from '@/lib/academic-year-context';
import { localizeApiErrorMessage } from '@/lib/utils';
import i18n from '@/i18n';

// Base URL includes /api/v1 prefix for all endpoints
const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
const instance = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});
instance.defaults.withCredentials = true;

const getPersistedSchoolId = () => {
  try {
    const school = JSON.parse(localStorage.getItem('school') || '{}');
    return school?.id ? String(school.id) : '';
  } catch {
    return '';
  }
};

const getSelectedAcademicYearId = () => {
  return selectedAcademicYearRequestId();
};

const normalizeApiPath = (url?: string) => {
  if (!url) return '';
  const path = url.split('?')[0].replace(/^https?:\/\/[^/]+\/api\/v1\//, '');
  return path.startsWith('/') ? path.slice(1).replace(/^api\/v1\//, '') : path;
};

const appendQueryParam = (url: string, key: string, value: string) => {
  const [withoutHash, hash] = url.split('#');
  const [path, query = ''] = withoutHash.split('?');
  const params = new URLSearchParams(query);

  if (!params.has(key)) {
    params.set(key, value);
  }

  return `${path}?${params.toString()}${hash ? `#${hash}` : ''}`;
};

const readRequestBody = (data: any) => {
  if (!data) return data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data;
};

const writeRequestBody = (original: any, next: any) => {
  if (typeof original === 'string') {
    return JSON.stringify(next);
  }
  return next;
};

const academicYearGetEndpointPatterns = [
  /^students\/list$/,
  /^students$/,
  /^students\/all-schools\/[^/]+$/,
  /^students\/grade\/[^/]+$/,
  /^students\/[^/]+$/,
  /^students\/user$/,
  /^students\/user\/[^/]+$/,
  /^students\/parent\/[^/]+$/,
  /^students\/[^/]+\/enrollment-history$/,
  /^students\/[^/]+\/teacher$/,
  /^students\/[^/]+\/dashboard-summary$/,
  /^parents\/[^/]+\/students$/,
  /^enrollments\/school\/[^/]+\/class\/[^/]+\/student$/,
  /^enrollments\/class\/[^/]+\/student$/,
  /^course-schedules\/class\/[^/]+$/,
  /^course-schedules\/global\/class\/[^/]+$/,
  /^course-schedules\/class\/[^/]+\/calendar$/,
  /^course-schedules\/class\/[^/]+\/calendar\/1$/,
  /^course-schedules\/class\/[^/]+\/calendar\/global$/,
  /^course-schedules\/student\/[^/]+$/,
  /^course-schedules\/teacher\/[^/]+$/,
  /^course-schedules\/teacher\/[^/]+\/today$/,
  /^exams\/global$/,
  /^exams\/global\/start-date\/[^/]+\/end-date\/[^/]+\/list$/,
  /^exams\/start-date\/[^/]+\/end-date\/[^/]+\/list$/,
  /^exams\/student\/[^/]+$/,
  /^exams\/teacher\/[^/]+$/,
  /^exams\/teacher\/archived$/,
  /^homework\/class\/[^/]+$/,
  /^homework\/student$/,
  /^homework\/child\/[^/]+$/,
  /^homework\/children$/,
  /^scores\/student\/class\/[^/]+\/exam\/[^/]+\/list$/,
  /^scores\/student\/class\/[^/]+\/exam\/[^/]+\/update\/list$/,
  /^scores\/record\/teacher\/[^/]+\/month\/[^/]+\/year\/[^/]+$/,
  /^student-reports\/student\/[^/]+\/period\/[^/]+$/,
  /^student-reports\/student\/[^/]+\/period\/[^/]+\/detail$/,
  /^student-reports\/class\/[^/]+\/period\/[^/]+$/,
  /^student-reports\/class\/[^/]+\/period\/[^/]+\/detail$/,
  /^student-reports\/grade\/[^/]+\/period\/[^/]+$/,
  /^courses$/,
  /^courses\/[^/]+\/classes$/,
  /^courses\/class\/[^/]+$/,
  /^teachers$/,
  /^teachers\/list$/,
  /^teachers\/grade\/[^/]+$/,
  /^teachers\/course\/[^/]+$/,
  /^teachers\/[^/]+\/pending-tasks$/,
  /^teachers\/[^/]+\/dashboard-summary$/,
  /^report-period$/,
  /^report-period\/type$/,
  /^dashboard\/summary$/,
  /^dashboard\/upcoming-exam$/,
  /^dashboard\/classes\/grade\/[^/]+$/,
];

const academicYearBodyEndpointPatterns = [
  /^(put):students\/[^/]+\/grade\/[^/]+\/class\/[^/]+$/,
  /^(post):courses\/assign-course-class$/,
  /^(post):courses\/unassign-course-class$/,
  /^(post):courses\/with-subject$/,
  /^(put):teachers\/change-grade$/,
  /^(put):teachers\/change-course$/,
];

const applyAcademicYearContext = (config: any) => {
  const academicYearId = getSelectedAcademicYearId();
  if (!academicYearId || !config.url) return config;

  const method = (config.method || 'get').toLowerCase();
  const path = normalizeApiPath(config.url);

  if (method === 'get' && academicYearGetEndpointPatterns.some(pattern => pattern.test(path))) {
    config.url = appendQueryParam(config.url, 'academic_year_id', academicYearId);
  }

  const bodyMatcherKey = `${method}:${path}`;
  if (academicYearBodyEndpointPatterns.some(pattern => pattern.test(bodyMatcherKey))) {
    const body = readRequestBody(config.data);
    if (body && typeof body === 'object' && !Array.isArray(body) && body.academic_year_id === undefined) {
      config.data = writeRequestBody(config.data, {
        ...body,
        academic_year_id: Number(academicYearId),
      });
    }
  }

  return config;
};

const isPublicEndpoint = (url?: string) => {
  const normalizedUrl = url.startsWith('/') ? url.slice(1) : url;

  return normalizedUrl.startsWith('auth/login') ||
    normalizedUrl.startsWith('auth/verify-2fa') ||
    normalizedUrl.startsWith('auth/verify-recovery') ||
    normalizedUrl.startsWith('auth/passkey/authenticate') ||
    normalizedUrl.startsWith('auth/user-lookup') ||
    normalizedUrl.startsWith('auth/resend-2fa') ||
    normalizedUrl.startsWith('auth/refresh') ||
    normalizedUrl.startsWith('auth/forgot-password') ||
    normalizedUrl.startsWith('auth/reset-password') ||
    normalizedUrl.startsWith('auth/verification/') ||
    normalizedUrl.startsWith('auth/register/school') ||
    normalizedUrl.includes('subscription-plans') ||
    normalizedUrl.includes('schools/type/list') ||
    normalizedUrl.includes('schools/registration/') ||
    normalizedUrl.includes('verification/');
};

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const decodeJwtPayload = (token: string) => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const isTokenExpiredOrExpiring = (token: string, skewSeconds = 30) => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;

  return payload.exp <= Math.floor(Date.now() / 1000) + skewSeconds;
};

const extractRefreshTokenData = (response: any) => {
  const data = response?.data ?? response;
  return data?.authorization ?? data?.data?.authorization ?? data?.data ?? data;
};

const refreshStoredToken = async () => {
  const rs = await authApi.refreshToken();
  const tokenData = extractRefreshTokenData(rs);
  const { access_token, refresh_token, firebase_token } = tokenData || {};

  if (!access_token) {
    throw { status: 401, message: 'Token refresh failed' };
  }

  const user = getStoredUser();
  user.access_token = access_token;
  if (refresh_token) {
    user.refresh_token = refresh_token;
  }
  if (firebase_token) {
    user.firebase_token = firebase_token;
  }
  localStorage.setItem('user', JSON.stringify(user));
  instance.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;

  return access_token;
};

// Track if we're currently refreshing the token
let isRefreshing = false;
// Store pending requests
let failedQueue: any[] = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

const clearAuthAndRedirectToLogin = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('school');
  clearAcademicYearHeader();
  localStorage.removeItem('branch');
  localStorage.removeItem('2fa_session');
  Cookies.remove('ssm-session');
  Cookies.remove('ssm-session-expire');

  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

instance.interceptors.request.use(
  async (config) => {

    const schoolStore = useSchoolStore();
    // const branchStore = useBranchStore();

    // Set device fingerprint header for all requests
    // try {
    //   const deviceFingerprint = getDeviceFingerprint();
    //   config.headers['X-Device-Fingerprint'] = deviceFingerprint;
    //
    //   // Debug logging for passkey authentication endpoints
    //   if (config.url?.includes('passkey') || config.url?.includes('auth/')) {
    //     console.log('API Request:', config.url, 'Device fingerprint:', deviceFingerprint.substring(0, 16) + '...');
    //   }
    // } catch (error) {
    //   console.warn('Failed to add device fingerprint header:', error);
    //
    //   // For critical auth endpoints, this could be a blocker
    //   if (config.url?.includes('passkey') || config.url?.includes('auth/')) {
    //     console.error('Critical: Device fingerprint missing for auth endpoint:', config.url);
    //   }
    // }

    // Set school ID header for all requests
    const schoolId = schoolStore.selectedSchool?.id ? String(schoolStore.selectedSchool.id) : getPersistedSchoolId();
    if (schoolId) {
      config.headers['X-School-ID'] = schoolId;
    }

    applyAcademicYearContext(config);

    // Set branch ID header for all requests (multi-branch support)
    // Ensure branch is loaded from localStorage if not already in store
    // Set branch ID header for all requests (multi-branch support)
    // Ensure branch is loaded from localStorage if not already in store
    // if (!branchStore.selectedBranch?.id) {
    //   branchStore.getSelectedBranch();
    // }
    // if (branchStore.selectedBranch?.id) {
    //   config.headers['X-Branch-ID'] = branchStore.selectedBranch.id;
    // }
    // Check session cookie for authentication (school check is now header-based)
    // const cookie = Cookies.get('ssm-session');
    // if (cookie === undefined &&
    //   !config?.url?.includes('schools/user') &&
    //   !config?.url?.includes('subscription-plans') &&
    //   !config?.url?.includes('schools/type/list') &&
    //   !config?.url?.includes('auth/')) {
    //   return Promise.reject({message: 'No such session'});
    // }
    if (isPublicEndpoint(config.url)) {
      return config;
    }

    const user = getStoredUser();
    if (user?.access_token && isTokenExpiredOrExpiring(user.access_token)) {
      if (isRefreshing) {
        await new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });
      } else {
        isRefreshing = true;
        try {
          await refreshStoredToken();
          processQueue();
        } catch (error) {
          processQueue(error);
          clearAuthAndRedirectToLogin();
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      }
    }

    const refreshedUser = getStoredUser();
    if (refreshedUser?.access_token) {
      config.headers['Authorization'] = 'Bearer ' + refreshedUser.access_token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (res) => {
    return res;
  },
  async (err) => {
    const originalConfig = err.config;

    if (!err.response) {
      return Promise.reject(err);
    }

// if (err.response.status === 426) {
//   Cookies.remove('app_version');
//   Cookies.remove('app_version', { path: '/' });
//   window.location.reload();
//   return Promise.reject(err);
// }

if (err.response.status === 401 && !originalConfig._retry && !isPublicEndpoint(originalConfig.url)) {
      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return instance(originalConfig);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalConfig._retry = true;
      isRefreshing = true;

      try {
        await refreshStoredToken();

        // Process all queued requests
        processQueue();

        // Retry the original request
        return instance(originalConfig);
      } catch (error) {
        console.log('Token refresh failed:', error)
        // Clear the queue with error
        processQueue(error);

        // Clean up auth data since refresh token is invalid
        clearAuthAndRedirectToLogin();

        return Promise.reject({
          status: 401,
          error_code: 'AUTHENTICATION_REQUIRED',
          message: 'Session expired, please login again'
        });
      } finally {
        isRefreshing = false;
      }
    }

    // For public endpoints with 401, just pass through the error
    if (err.response.status === 401 && isPublicEndpoint(originalConfig.url)) {
      return Promise.reject(err);
    }

    return Promise.reject(err);
  }
);

// Legacy callers still display AxiosError.message directly. Keep those toasts
// localized while callers move to localizedApiError(error, t).
instance.interceptors.response.use(undefined, (error) => {
  return Promise.reject(localizeApiErrorMessage(error, i18n.global.t));
});

export default instance;
